import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, Send, Users, UserPlus, LogOut, Flame, Crown } from "lucide-react";
import PhoneFrame from "../components/PhoneFrame.jsx";
import api from "../lib/api.js";
import { useAuth } from "../lib/auth.jsx";

export default function CircleDetail() {
  const { circleId } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [circle, setCircle] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [tab, setTab] = useState("chat");
  const [addQuery, setAddQuery] = useState("");
  const [results, setResults] = useState([]);
  const endRef = useRef(null);
  const pollRef = useRef(null);

  const load = async () => {
    try {
      const [c, m] = await Promise.all([
        api.get(`/community/circles/${circleId}`),
        api.get(`/community/circles/${circleId}/messages`),
      ]);
      setCircle(c.data);
      setMessages(m.data);
    } catch (e) {
      toast.error("Cannot load circle");
      nav("/community");
    }
  };

  useEffect(() => {
    load();
    pollRef.current = setInterval(load, 10000);
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [circleId]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async () => {
    if (!text.trim()) return;
    const t = text.trim();
    setText("");
    try {
      await api.post(`/community/circles/${circleId}/messages`, { text: t });
      load();
    } catch (e) {
      toast.error("Send failed");
    }
  };

  const doSearch = async () => {
    if (!addQuery.trim()) return setResults([]);
    const r = await api.get("/friends/search", { params: { q: addQuery.trim() } });
    const memberIds = new Set((circle?.members || []).map((m) => m.id));
    setResults(r.data.filter((u) => !memberIds.has(u.id)));
  };

  const addMember = async (uid) => {
    try {
      await api.post(`/community/circles/${circleId}/members/add`, { user_id: uid });
      toast.success("Added to circle");
      setAddQuery(""); setResults([]);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const leave = async () => {
    if (!window.confirm("Leave this circle?")) return;
    await api.post(`/community/circles/${circleId}/leave`);
    toast("Left circle");
    nav("/community");
  };

  if (!circle) return <PhoneFrame><div className="p-6 text-slate-400">Loading…</div></PhoneFrame>;

  return (
    <PhoneFrame>
      <div className="flex flex-col h-screen">
        <div className="px-5 pt-6 pb-3 border-b border-[#1a2f40]">
          <div className="flex items-center justify-between">
            <button onClick={() => nav("/community")} className="text-slate-400" data-testid="circle-back"><ChevronLeft size={20} /></button>
            <button onClick={leave} data-testid="circle-leave" className="text-xs text-slate-400 inline-flex items-center gap-1">
              <LogOut size={14} /> Leave
            </button>
          </div>
          <div className="mt-3 flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-[#142534] flex items-center justify-center">
              <Users size={20} color="#2fbf71" />
            </div>
            <div>
              <div className="ps-heading text-xl font-bold" data-testid="circle-name">{circle.name}</div>
              <div className="text-xs text-slate-400">{circle.members.length} member{circle.members.length !== 1 ? "s" : ""} · {circle.description || "Accountability circle"}</div>
            </div>
          </div>

          <div className="mt-3 flex gap-2 bg-[#0e1d2a] border border-[#1a2f40] rounded-full p-1">
            {[
              { k: "chat", l: "Chat" },
              { k: "members", l: `Members · ${circle.members.length}` },
            ].map((t) => (
              <button
                key={t.k}
                data-testid={`circle-tab-${t.k}`}
                onClick={() => setTab(t.k)}
                className={`flex-1 py-1.5 rounded-full text-xs font-semibold ${tab === t.k ? "bg-[#2fbf71] text-[#0a1822]" : "text-slate-400"}`}
              >{t.l}</button>
            ))}
          </div>
        </div>

        {tab === "chat" && (
          <>
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-2" data-testid="circle-messages">
              {messages.length === 0 && (
                <div className="text-center text-slate-500 text-sm py-10">No messages yet. Be the first to say hi 👋</div>
              )}
              {messages.map((m) => {
                const mine = m.user_id === user?.id;
                return (
                  <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"} items-end gap-2`}>
                    {!mine && (
                      <div className="w-7 h-7 rounded-full bg-[#142534] border border-[#1a2f40] flex items-center justify-center text-[10px] font-bold shrink-0">
                        {m.sender?.name?.[0] ?? "?"}
                      </div>
                    )}
                    <div className={`max-w-[72%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed ${
                      mine ? "bg-[#2fbf71] text-[#0a1822]" : "bg-[#142534] border border-[#1a2f40]"
                    }`}>
                      {!mine && <div className="text-[10px] font-bold text-[#2fbf71] mb-0.5">{m.sender?.name}</div>}
                      {m.text}
                    </div>
                  </div>
                );
              })}
              <div ref={endRef} />
            </div>
            <div className="p-3 border-t border-[#1a2f40] flex gap-2">
              <input
                data-testid="circle-input"
                value={text} onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="Message your circle…"
                className="flex-1 bg-[#142534] border border-[#1a2f40] rounded-full px-4 py-3 text-sm text-white outline-none focus:border-[#2fbf71]"
              />
              <button
                data-testid="circle-send"
                disabled={!text.trim()}
                onClick={send}
                className="w-12 h-12 rounded-full bg-[#2fbf71] text-[#0a1822] flex items-center justify-center disabled:opacity-50"
              >
                <Send size={18} />
              </button>
            </div>
          </>
        )}

        {tab === "members" && (
          <div className="flex-1 overflow-y-auto px-5 py-4">
            <div className="space-y-2">
              {circle.members.map((m) => (
                <div key={m.id} className="ps-card p-3 flex items-center gap-3" data-testid={`circle-member-${m.id}`}>
                  <div className="w-10 h-10 rounded-full bg-[#142534] border border-[#1a2f40] flex items-center justify-center font-bold">{m.name?.[0]}</div>
                  <div className="flex-1">
                    <div className="font-semibold text-sm flex items-center gap-1">
                      {m.name}
                      {m.is_owner && <Crown size={12} color="#F59E0B" />}
                    </div>
                    <div className="text-xs text-slate-400 inline-flex items-center gap-1">
                      <Flame size={10} color="#F59E0B" /> {m.streak}d · {m.points} pts
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6">
              <div className="text-xs uppercase tracking-widest text-slate-400 font-bold mb-2">Add a member</div>
              <div className="flex gap-2">
                <input
                  data-testid="circle-add-input"
                  value={addQuery} onChange={(e) => setAddQuery(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && doSearch()}
                  placeholder="Search name or email"
                  className="flex-1 bg-[#142534] border border-[#1a2f40] rounded-xl px-4 py-2.5 text-sm outline-none focus:border-[#2fbf71]"
                />
                <button data-testid="circle-add-search" onClick={doSearch} className="ps-button-primary text-xs px-4">Search</button>
              </div>
              {circle.members.length >= 10 && <div className="text-xs text-[#F59E0B] mt-2">Circle full (10/10).</div>}
              <div className="mt-3 space-y-2">
                {results.map((u) => (
                  <div key={u.id} className="ps-card p-3 flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-[#142534] border border-[#1a2f40] flex items-center justify-center text-sm font-bold">{u.name?.[0]}</div>
                    <div className="flex-1 text-sm">
                      <div className="font-semibold">{u.name}</div>
                      <div className="text-xs text-slate-400">{u.email}</div>
                    </div>
                    <button
                      data-testid={`circle-add-${u.id}`}
                      disabled={circle.members.length >= 10}
                      onClick={() => addMember(u.id)}
                      className="ps-button-primary text-xs py-1.5 px-3 inline-flex items-center gap-1 disabled:opacity-50"
                    >
                      <UserPlus size={12} /> Add
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </PhoneFrame>
  );
}
